import React, { useState, useCallback, useEffect } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { GoalsOverviewPage } from '../ui/diary/GoalsOverviewPage';
import type {
  GoalsOverviewData,
  GoalsOverviewContainerProps,
} from '../shared/types/goals';

export type { GoalsOverviewContainerProps };

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

const GOALS_OVERVIEW_QUERY = `
  query GoalsOverview($weekOffset: Int!) {
    goalsOverview(weekOffset: $weekOffset) {
      periodGroups {
        periodType
        periodLabel
        goals {
          fieldName
          targetCount
          actualCount
          period
          completionPercentage
          achieved
        }
      }
      weekOffset
      canNavigateToPreviousWeek
      canNavigateToNextWeek
      categories {
        id
        name
        items {
          id
          name
          type
          hasGoal
          icon
          fields {
            id
            name
          }
        }
      }
    }
  }
`;

interface GoalsOverviewPageContainerInnerProps extends GoalsOverviewContainerProps {
  initialWeekOffset?: number;
}

function GoalsOverviewPageContainerInner({
  graphqlEndpoint,
  csrfToken,
  labels,
  achievementsUrl,
  initialWeekOffset = 0,
}: GoalsOverviewPageContainerInnerProps) {
  const [weekOffset, setWeekOffset] = useState(initialWeekOffset);
  const [view, setView] = useState<'overview' | 'select_category'>('overview');

  const { data, isLoading, refetch } = useQuery<GoalsOverviewData>({
    queryKey: ['goalsOverview', weekOffset],
    queryFn: async () => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: GOALS_OVERVIEW_QUERY,
          variables: { weekOffset },
        }),
      });

      if (!response.ok) {
        throw new Error('Network error');
      }

      const result = await response.json();

      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.goalsOverview;
    },
  });

  const handleWeekOffsetChange = useCallback((newOffset: number) => {
    setWeekOffset(newOffset);
  }, []);

  const handleCreateGoalClick = useCallback(() => {
    setView('select_category');
  }, []);

  const handleBackToOverview = useCallback(() => {
    setView('overview');
  }, []);

  const handleSelectField = useCallback(
    (categoryId: string, itemId: string, type: 'toggle_field' | 'field_set') => {
      // Navigate to the goal creation page for the selected field or field set
      const basePath = '/diary/categories';
      if (type === 'field_set') {
        window.location.href = `${basePath}/${categoryId}/field_sets/${itemId}/goal/new`;
      } else {
        window.location.href = `${basePath}/${categoryId}/toggle_fields/${itemId}/goal/new`;
      }
    },
    []
  );

  // Refetch when week offset changes
  useEffect(() => {
    refetch();
  }, [weekOffset, refetch]);

  return (
    <GoalsOverviewPage
      periodGroups={data?.periodGroups || []}
      weekOffset={weekOffset}
      canNavigateToPreviousWeek={data?.canNavigateToPreviousWeek ?? true}
      canNavigateToNextWeek={data?.canNavigateToNextWeek ?? false}
      categories={data?.categories || []}
      onWeekOffsetChange={handleWeekOffsetChange}
      onCreateGoalClick={handleCreateGoalClick}
      onSelectField={handleSelectField}
      labels={labels}
      achievementsUrl={achievementsUrl}
      isLoading={isLoading}
      view={view}
      onBackToOverview={handleBackToOverview}
    />
  );
}

function GoalsOverviewPageContainer(props: GoalsOverviewContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <GoalsOverviewPageContainerInner {...props} />
    </QueryClientProvider>
  );
}

// Store root reference for cleanup
let pageRoot: Root | null = null;

/**
 * Mount function for rendering from Rails
 */
export function mountGoalsOverviewPage(
  container: HTMLElement,
  props: GoalsOverviewContainerProps
): Root {
  if (pageRoot) {
    pageRoot.unmount();
  }

  const root = createRoot(container);
  pageRoot = root;
  root.render(<GoalsOverviewPageContainer {...props} />);
  return root;
}

/**
 * Unmount function for cleanup
 */
export function unmountGoalsOverviewPage(): void {
  if (pageRoot) {
    pageRoot.unmount();
    pageRoot = null;
  }
}

export default GoalsOverviewPageContainer;
